import React, { useMemo } from "react"
import { cva } from "class-variance-authority"
import cn from "../../../utils/classes"
import SkeletonLoader from "./SkeletonLoader"
import BubbleIndicator, { BubbleIndicatorProps } from "./BubbleIndicator"

type ThumbnailSize = "sm" | "md" | "lg"

export interface ThumbnailProps {
  /** The desired size of the thumbnail. */
  size?: ThumbnailSize
  /** The source image url string. */
  imageUrl: string
  /** The alternate text for the image. */
  imageAltText: string
  /** The key used for accessibility. */
  id: string
  /** Additional CSS classes to override the default thumbnail container styles. */
  styleOverrides?: string
  /** Additional CSS classes to override the default image styles. */
  imageStyleOverrides?: string
  /** The aria label used for accessibility. */
  ariaLabel: string
  /** The boolean used to show the loading state. */
  isLoading?: boolean
  /** The callback function to be invoked when the thumbnail is clicked. */
  onClick?: () => void
  /** The props for the BubbleIndicator component. */
  bubbleIndicator?: BubbleIndicatorProps
}

const Thumbnail: React.FC<ThumbnailProps> = ({
  size = "md",
  imageUrl,
  imageAltText,
  id,
  styleOverrides,
  imageStyleOverrides,
  ariaLabel,
  isLoading = false,
  onClick,
  bubbleIndicator,
}) => {
  const variants = cva(
    ["relative", "border-none", "bg-transparent", "p-0", "m-0", "h-fit"],
    {
      variants: {
        size: {
          sm: "w-20 h-16",
          md: "w-32 h-24",
          lg: "w-48 h-32",
        },
      },
    },
  )

  const cursorStyle =
    typeof onClick === "function" ? "cursor-pointer" : "cursor-default"

  const containerClassName = useMemo(() => {
    return cn(variants({ size }), cursorStyle, styleOverrides)
  }, [cursorStyle, size, styleOverrides, variants])

  const imageClassName = useMemo(() => {
    return cn(
      ["rounded-md", "object-cover", "w-full", "h-full"],
      cursorStyle,
      imageStyleOverrides,
    )
  }, [cursorStyle, imageStyleOverrides])

  const thumbnailComponent = (
    <>
      <img
        className={imageClassName}
        src={imageUrl!}
        alt={imageAltText}
        aria-label={ariaLabel}
      />
      {!isLoading && !!bubbleIndicator && (
        <div className="absolute top-0 right-0 transform -translate-y-1/3 translate-x-1/3">
          <BubbleIndicator {...bubbleIndicator} />
        </div>
      )}
    </>
  )

  if (isLoading) {
    return (
      <div className={containerClassName}>
        <SkeletonLoader height={"100%"} width={"100%"} />
      </div>
    )
  } else if (!!onClick) {
    return (
      <button id={id} className={containerClassName} onClick={onClick}>
        {thumbnailComponent}
      </button>
    )
  } else {
    return (
      <div id={id} className={containerClassName}>
        {thumbnailComponent}
      </div>
    )
  }
}

export default Thumbnail
